import {
    ActionCreatorWithPayload,
    ThunkDispatch,
    createAction,
} from '@reduxjs/toolkit';

import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { NavigateFunction } from 'react-router';

import { LOCALSTORAGE } from 'config';
import { Loadable, ErrorTypes, AuthManager } from 'models';
import { RootState } from 'store';

import { AuthActions } from './auth';

export type AppThunkAction<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
>;

export interface LoadableAction<T, U = void> {
    loadable: Loadable<T>;
    input: U;
}

export const handleLogin = async (
    dispatch: ThunkDispatch<RootState, void, Action<any>>,
    auth: RootState['auth'],
    navigate: NavigateFunction,
): Promise<boolean> => {
    const urlParams = new URLSearchParams(window.location.search);
    const storedAuthStr = localStorage.getItem(LOCALSTORAGE.AUTH);
    let usingStoredToken = false;

    const { tokens, user } = auth;

    if (storedAuthStr && tokens.status === 'idle') {
        const storedAuth = JSON.parse(storedAuthStr) as AuthManager.OAuth2Creds;

        // check if we have an access token and it still appears to be valid
        if (
            storedAuth?.accessToken &&
            storedAuth.refreshTime &&
            new Date() < new Date(storedAuth.refreshTime)
        ) {
            const fixedStoredAuth = {
                ...storedAuth,
                refreshTime: new Date(storedAuth.refreshTime),
                refreshRefreshTokenTime: storedAuth.refreshRefreshTokenTime
                    ? new Date(storedAuth.refreshRefreshTokenTime)
                    : undefined,
            };
            dispatch(AuthActions.setTokensStatus({ status: 'finished' }));
            dispatch(AuthActions.setTokens(fixedStoredAuth));

            usingStoredToken = true;

            // now that we have auth, we can test this by getting user details
            if (user.status === 'idle') {
                dispatch(AuthActions.getSelf())
                    .then(() =>
                        dispatch(
                            AuthActions.getToken({
                                tokens: fixedStoredAuth,
                            }),
                        ),
                    )
                    .catch(() => {
                        // something wrong with token, should reattempt a login here
                        // for now as a quick solution (until the code in following parts are cleaned),
                        // remove the auth in localstorage and refresh

                        localStorage.removeItem(LOCALSTORAGE.AUTH);
                        AuthManager.redirectToAuth();
                    });
            }
        }
    }

    if (
        auth.tokens.status === 'idle' &&
        auth.tokens.object == null &&
        !usingStoredToken
    ) {
        // Check if code has been sent
        const code = urlParams.get('code');
        const redirectPath = urlParams.get('state');

        const errorMessage = urlParams.get('error');
        if (errorMessage) {
            dispatch(
                AuthActions.setTokensStatus({
                    status: 'error',
                    error: {
                        code: 403,
                        error: errorMessage,
                    },
                }),
            );
            return false;
        }

        if (code) {
            if (window.history.pushState) {
                if (redirectPath) {
                    const path = JSON.parse(redirectPath)?.path;
                    navigate(path ?? window.location.origin);
                } else {
                    window.history.pushState(
                        { path: window.location.origin },
                        '',
                        window.location.origin,
                    );
                }
            }

            // use code to get Token, then get  User
            dispatch(AuthActions.getToken({ authCode: code })).then(() => {
                // now that we have auth, we can test this by getting user details
                if (user.status === 'idle') {
                    dispatch(AuthActions.getSelf())
                        .then(() => {})
                        .catch(() => {
                            // Here be a successful login but unable to get user info
                        });
                }
            });
        } else {
            console.log('Redirecting to login page');
            // Redirect to login page
            AuthManager.redirectToAuth();
        }
    }
    return true;
};

export function makeThunkFromAPICall<Obj, Payload>(
    apiCall: (
        payload: Payload,
        state?: RootState,
        dispatch?: ThunkDispatch<RootState, unknown, Action<string>>,
    ) => Promise<Obj>,
    type: string,
    requireAuth = true,
): [
    (payload: Payload) => AppThunkAction<Promise<Obj>>,
    ActionCreatorWithPayload<LoadableAction<Obj, Payload>, string>,
] {
    const setterAction = <
        ActionCreatorWithPayload<LoadableAction<Obj, Payload>, string>
    >createAction(type);
    return [
        (payload) => async (dispatch, getState) => {
            const dispatchSetter = (loadable: Loadable<Obj>) =>
                setterAction &&
                dispatch(setterAction({ loadable, input: payload }));
            const state = getState();
            dispatchSetter({ status: 'loading' });
            try {
                const auth = state.auth.tokens.object;
                if (!requireAuth || (requireAuth && auth)) {
                    const obj = await apiCall(payload, state, dispatch);
                    dispatchSetter({
                        object: obj,
                        status: 'finished',
                    });
                    return obj;
                }
                const err = {
                    error: 'No Credentials available',
                    code: ErrorTypes.INVALID_CREDENTIALS,
                };
                dispatchSetter({
                    error: err,
                    status: 'error',
                });
                throw err;
            } catch (err: any) {
                dispatchSetter({
                    error: err,
                    status: 'error',
                });
                throw err;
            }
        },
        setterAction,
    ];
}
